import { useEffect, useMemo, useState } from 'react';
import { Card } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import AnalyticsApiService, { PostObservabilityOverviewTimeseriesPoint } from '../../services/analyticsApi';
import { DateRangePicker } from './DateRangePicker';
import { DateRange } from 'react-day-picker';
import { parseLocalDate } from '../../utils/dateParser';
import { buildDailySeries, sumByUtcDay } from '../../utils/dailyTimeseries';

interface PostReplyRateChartProps {
  className?: string;
  days?: 7 | 14 | 30;
  showControls?: boolean;
}

export function PostReplyRateChart({ className, days: propDays, showControls = true }: PostReplyRateChartProps) {
  const [data, setData] = useState<Array<{ date: string; impressions: number }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);

    const start = new Date();
    start.setDate(start.getDate() - (propDays || 7));
    start.setHours(0, 0, 0, 0);

    return { from: start, to: end };
  });

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      if (!dateRange?.from || !dateRange?.to) {
        setData([]);
        return;
      }

      const startOfDay = new Date(dateRange.from);
      startOfDay.setHours(0, 0, 0, 0);

      const endOfDay = new Date(dateRange.to);
      endOfDay.setHours(23, 59, 59, 999);

      const fromIso = startOfDay.toISOString();
      const toIso = endOfDay.toISOString();

      const overviewTs = await AnalyticsApiService.getPostObservabilityOverviewTimeseries(fromIso, toIso);
      const impressionsByDay = sumByUtcDay<PostObservabilityOverviewTimeseriesPoint>(
        overviewTs.points || [],
        (p) => p.bucket_start,
        (p) => Number(p.impressions || 0)
      );

      const daily = buildDailySeries(
        fromIso,
        toIso,
        { impressions: impressionsByDay },
        { impressions: 0 }
      );

      setData(daily);
    } catch (e) {
      console.error('Failed to fetch post reply rate:', e);
      setError('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange?.from, dateRange?.to]);

  useEffect(() => {
    const id = setInterval(fetchData, 3600000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange?.from, dateRange?.to]);

  const formatXAxis = (dateStr: string) => {
    const d = parseLocalDate(dateStr);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const d = parseLocalDate(label);
    const point = payload?.[0]?.payload as { date: string; impressions: number } | undefined;
    return (
      <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border border-fuchsia-200 dark:border-fuchsia-700 rounded-xl p-3 shadow-xl">
        <p className="font-['SF_Pro_Text',_-apple-system,_sans-serif] font-semibold text-gray-900 dark:text-gray-100 text-xs mb-2">
          {d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
        </p>
        <p className="font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-fuchsia-600 dark:text-fuchsia-400 text-lg">
          {(point?.impressions ?? 0).toLocaleString()} impressions
        </p>
      </div>
    );
  };

  const stats = useMemo(() => {
    if (!data.length) return { avg: 0, max: 0, min: 0 };
    const values = data.map((d) => d.impressions || 0);
    const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
    const max = Math.max(...values);
    const min = Math.min(...values);
    return { avg, max, min };
  }, [data]);
  const hasData = data.length > 0;

  return (
    <Card
      className={`bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border border-gray-200/50 dark:border-gray-800/50 shadow-xl overflow-hidden ${className}`}
    >
      <div className="p-6 pb-4 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-fuchsia-100 dark:bg-fuchsia-900/50 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-fuchsia-600 dark:text-fuchsia-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 7h10M7 11h10M7 15h6M5 3h14a2 2 0 012 2v14l-4-3H5a2 2 0 01-2-2V5a2 2 0 012-2z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-gray-900 dark:text-gray-100">
                  Daily total impressions
                </h3>
                <p className="text-sm font-['SF_Pro_Text',_-apple-system,_sans-serif] text-gray-600 dark:text-gray-400">
                  Daily impressions counter
                </p>
              </div>
            </div>

            <div className="flex items-center gap-6 mt-4">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Average</p>
                <p className="text-2xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-fuchsia-600 dark:text-fuchsia-400">
                  {hasData ? stats.avg.toFixed(0) : '—'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Peak</p>
                <p className="text-2xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-gray-700 dark:text-gray-300">
                  {hasData ? stats.max.toFixed(0) : '—'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Low</p>
                <p className="text-2xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-gray-700 dark:text-gray-300">
                  {hasData ? stats.min.toFixed(0) : '—'}
                </p>
              </div>
            </div>
          </div>

          {showControls && (
            <DateRangePicker value={dateRange} onChange={setDateRange} showDateText={false} className="w-auto" />
          )}
        </div>
      </div>

      <div className="p-6 pt-4">
        {isLoading ? (
          <Skeleton className="h-64 w-full bg-gray-200/60 dark:bg-gray-700/60 rounded-xl" />
        ) : error ? (
          <div className="h-64 flex items-center justify-center">
            <p className="text-red-500 dark:text-red-400">{error}</p>
          </div>
        ) : !hasData ? (
          <div className="h-64 flex items-center justify-center rounded-xl border border-dashed border-gray-200/60 dark:border-gray-700/60 bg-gray-50/30 dark:bg-gray-800/10">
            <div className="text-center px-6">
              <p className="text-sm font-['SF_Pro_Text',_-apple-system,_sans-serif] font-semibold text-gray-900 dark:text-gray-100">
                No post reply-rate data in this range
              </p>
              <p className="text-xs font-['SF_Pro_Text',_-apple-system,_sans-serif] text-gray-600 dark:text-gray-400 mt-1">
                Try selecting an earlier date range.
              </p>
            </div>
          </div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.3} vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatXAxis}
                  tick={{ fontSize: 11, fill: '#9ca3af', fontFamily: 'SF Pro Text, -apple-system, sans-serif' }}
                  stroke="#e5e7eb"
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#9ca3af', fontFamily: 'SF Pro Text, -apple-system, sans-serif' }}
                  stroke="#e5e7eb"
                />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="impressions"
                  stroke="#c026d3"
                  strokeWidth={3}
                  dot={{ r: 4, fill: '#c026d3', strokeWidth: 2, stroke: '#ffffff' }}
                  activeDot={{ r: 6, fill: '#c026d3' }}
                  animationDuration={1000}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </Card>
  );
}


